import { type AxiosResponse } from 'axios'
import type { IAPiOption } from 'shared/api'
import { baseApi } from 'entities/Auth'
import {
  type IResponseGetEinData,
  type IParamsOptInTypes,
  type IResponseProvider,
  type IResponseCountryState,
  type IParamsSetBpBusinessPointsOfContact,
  type IParamsSetBpBusinessTerms,
  type IResponseComplianceSummary,
  type IParamsSaveComplianceSummary,
  type IParamsSetBpBusinessInformation,
  type IParamsSubmitLocalNumberApplication,
  type IParamsSetCampaignPackageData,
  type IResponseOptInUpload,
  type IResponseStockExchanges,
  type IResponseGetComplianceServicesList,
  type IResponseOrganizationBusinessProfile,
  type IParamsUpdateComplianceServicesCampaign,
  type IResponseComplianceServicesCampaign,
  type IResponseComplianceServicesBrandPackage,
  type IResponseOrganizationTollFree,
  type IResponseOrganizationTollFreeFormFields,
  type IParamsSetBpBusinessInformationLocation,
  type IIrsEinDocument,
  type IOnlineFormUrlCheck,
  type IResponseOnlineFormUrlCheck,
  type IOptInMethodNames,
  type IParamsUpdateTollFreeBusinessInfo,
  type IParamUpdateTollFreeContactInfo,
  type IParamUpdateTollFreeUseCase,
  type IParamUpdateTollFreeOptInMethod,
  type IResponseOptInType,
  type IParamUpdateTollFreeOptInConsent,
  type IResponseOrganizationTollFreeUpdate,
  type IParamsSetCampaignTollFreeOptInFiles,
  type IParamsTollFreeDeleteAttachment,
  type IResponseTollFreeOptInUpload,
  type IParamsDeleteAttachment,
  type IParamsGenerateAIDesc,
  type IResponseGenerateAI,
  type IParamsGenerateAIMessage,
  type IParamsGenerate10DLCAIDesc,
  type IResponseGenerate10DLCAIDesc,
  type IParamsGetFreeTollFreeNumbers,
  type IResponseFreeTollFreeNumber,
  type IParamsAnalyze10DLCAI,
  type IResponseAnalyze10DLCAI,
  type IParamsAddTollFreeNumberWithNewInbox,
  type IParamsAddTollFreeNumberToExistingInbox,
  type IParamsSubmitTollFreeForReview,
  type IResponseTollFree,
  type IParamsGenerate10DLCAIMessage,
  type IResponseGenerate10DLCAIMessage,
  type IResponseNumberVendor,
  type IResponseInternationalRegulatoryBundle,
  type IParamsGetInternationalRegulatoryBundle,
  type IResponseInternationalComplianceRegulations,
  type IParamsSubmitBusinessDataInternationalRegulatory,
  type IResponseSubmitDataInternationalRegulatory,
  type IParamsSubmitAddressDataInternationalRegulatory,
  type IParamsSubmitAuthorizedRepresentativeDataInternationalRegulatory,
  type IParamsSubmitAInternationalRegulatory,
} from 'entities/Compliance/api/types/complianceType'
import { type IResponseNumber } from 'entities/Phone/api/types'
import { type IParamsResubmitTollFree } from 'entities/Admin/compliance'

class Api {
  getOptInTypes({ filter }: IParamsOptInTypes): Promise<AxiosResponse<IResponseOptInType[]>> {
    return baseApi.get('organization/opt-in-data/types', {
      params: {
        filter,
      },
    })
  }

  getOptInMethodNames(): Promise<AxiosResponse<IOptInMethodNames[]>> {
    return baseApi.get('compliance/enum/opt-in-methods')
  }

  getBusinessProfileData(): Promise<AxiosResponse<IResponseOrganizationBusinessProfile>> {
    return baseApi.get('organization/business-profile')
  }

  getBpStates(): Promise<AxiosResponse<IResponseCountryState[]>> {
    return baseApi.get('organization/business-profile/states')
  }

  getComplianceSummary(): Promise<AxiosResponse<IResponseComplianceSummary>> {
    return baseApi.get('ten-dlc/summary')
  }

  saveComplianceSummary(
    params: IParamsSaveComplianceSummary
  ): Promise<AxiosResponse<IResponseComplianceSummary>> {
    return baseApi.post('ten-dlc/summary', params)
  }

  setBpBusinessInformation(
    params: IParamsSetBpBusinessInformation
  ): Promise<AxiosResponse<IResponseOrganizationBusinessProfile>> {
    return baseApi.post('organization/business-profile/business-information', params)
  }

  setBpBusinessInformationLocation(
    params: IParamsSetBpBusinessInformationLocation
  ): Promise<AxiosResponse<IResponseOrganizationBusinessProfile>> {
    return baseApi.post('organization/business-profile/business-information', params)
  }

  setBpBusinessInformationEinDocument(file: File): Promise<AxiosResponse<IIrsEinDocument>> {
    const formData = new FormData()
    formData.append('irs_ein_doc', file, file.name)
    formData.append('fileName', file.name)
    formData.append('fileSize', file.size.toString())

    return baseApi.post(
      'organization/business-profile/business-information/ein-document',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
  }
  deleteBpBusinessInformationEinDocument() {
    return baseApi.delete('organization/business-profile/business-information/ein-document')
  }
  setBpPointsOfContact(
    params: IParamsSetBpBusinessPointsOfContact
  ): Promise<AxiosResponse<IResponseOrganizationBusinessProfile>> {
    return baseApi.post('organization/business-profile/points-of-contact', params)
  }
  setBpTerms(
    params: IParamsSetBpBusinessTerms
  ): Promise<AxiosResponse<IResponseOrganizationBusinessProfile>> {
    return baseApi.post('organization/business-profile/terms', params)
  }
  getBusinessProfileBusinessTypes(): Promise<AxiosResponse<IAPiOption[]>> {
    return baseApi.get('organization/business-profile/business-types')
  }
  getBusinessProfileJobPositions(): Promise<AxiosResponse<IAPiOption[]>> {
    return baseApi.get('organization/business-profile/job-positions')
  }
  getBusinessProfileIndustries(): Promise<AxiosResponse<IAPiOption[]>> {
    return baseApi.get('organization/business-profile/industries')
  }
  getBusinessProfileRegionsOfOperation(): Promise<AxiosResponse<IAPiOption[]>> {
    return baseApi.get('organization/business-profile/regions-of-operation')
  }
  getBusinessProfileBusinessRegistrationTypes(): Promise<AxiosResponse<IAPiOption[]>> {
    return baseApi.get('organization/business-profile/business-registration-types')
  }
  getBusinessProfileStockExchanges(): Promise<AxiosResponse<IAPiOption[]>> {
    return baseApi.get('organization/business-profile/stock-exchanges')
  }
  getProviders(): Promise<AxiosResponse<Array<IResponseProvider>>> {
    return baseApi.get('ten-dlc/providers/list ')
  }
  getEinData({ ein }: { ein: string }): Promise<AxiosResponse<IResponseGetEinData>> {
    return baseApi.get('organization/business-profile/verify-ein', {
      params: {
        q: ein,
      },
    })
  }
  getOrganizationTollFree(): Promise<AxiosResponse<IResponseOrganizationTollFree>> {
    return baseApi.get('organization/toll-free')
  }
  deleteOrganizationTollFree(id: number): Promise<AxiosResponse<IResponseOrganizationTollFree>> {
    return baseApi.delete(`organization/toll-free/${id}`)
  }
  updateTollFreeNumbers(
    numbers: number[],
    id?: number | null
  ): Promise<AxiosResponse<IResponseOrganizationTollFreeUpdate>> {
    const idPath = id ? `/${id}` : ''

    return baseApi.post(`organization/toll-free/profile/numbers${idPath}`, { numbers })
  }
  updateTollFreeBusinessInfo(
    params: IParamsUpdateTollFreeBusinessInfo,
    id: number
  ): Promise<AxiosResponse<IResponseOrganizationTollFreeUpdate>> {
    return baseApi.post(`organization/toll-free/profile/${id}/business-info`, params)
  }
  updateTollFreeContactInfo(
    params: IParamUpdateTollFreeContactInfo,
    id: number
  ): Promise<AxiosResponse<IResponseOrganizationTollFreeUpdate>> {
    return baseApi.post(`organization/toll-free/profile/${id}/contact-person`, params)
  }
  updateTollFreeUseCase(
    params: IParamUpdateTollFreeUseCase,
    id: number
  ): Promise<AxiosResponse<IResponseOrganizationTollFreeUpdate>> {
    return baseApi.post(`organization/toll-free/profile/${id}/use-case`, params)
  }
  updateTollFreeOptInMethod(
    params: IParamUpdateTollFreeOptInMethod,
    id: number
  ): Promise<AxiosResponse<IResponseOrganizationTollFreeUpdate>> {
    return baseApi.post(`organization/toll-free/profile/${id}/opt-in-method`, params)
  }
  updateTollFreeOptInConsent(
    params: IParamUpdateTollFreeOptInConsent,
    id: number
  ): Promise<AxiosResponse<IResponseOrganizationTollFreeUpdate>> {
    return baseApi.post(`organization/toll-free/profile/${id}/opt-in-consent`, params)
  }
  submitTollFree(id: number): Promise<AxiosResponse<IResponseOrganizationTollFree>> {
    return baseApi.get(`organization/toll-free/verify/${id}`)
  }

  resubmitTollFree(
    params: IParamsResubmitTollFree
  ): Promise<AxiosResponse<IResponseOrganizationTollFree>> {
    return baseApi.post('organization/toll-free/resubmit-verification', params)
  }

  getOrganizationTollFreeFormFields(): Promise<
    AxiosResponse<IResponseOrganizationTollFreeFormFields>
  > {
    return baseApi.get('organization/toll-free/form-fields')
  }

  getComplianceServicesList(): Promise<AxiosResponse<IResponseGetComplianceServicesList>> {
    return baseApi.get('compliance/services/list')
  }

  onlineFormUrlCheck(
    params: IOnlineFormUrlCheck
  ): Promise<AxiosResponse<IResponseOnlineFormUrlCheck>> {
    return baseApi.post('compliance/services/campaign/online-form-url-check', params)
  }

  updateComplianceServicesCampaign(
    params: IParamsUpdateComplianceServicesCampaign
  ): Promise<AxiosResponse<IResponseComplianceServicesCampaign>> {
    return baseApi.post('compliance/services/campaign', params)
  }

  setCampaignPackageData(
    params: IParamsSetCampaignPackageData
  ): Promise<AxiosResponse<IResponseComplianceServicesBrandPackage>> {
    return baseApi.post('compliance/services/brand/package', params)
  }

  setCampaignOptInFiles(file: File): Promise<AxiosResponse<IResponseOptInUpload[]>> {
    return baseApi.post(
      'compliance/services/campaign/opt-in-files/upload',
      {
        image: file,
      },
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
  }

  setCampaignTollFreeOptInFiles(
    params: IParamsSetCampaignTollFreeOptInFiles
  ): Promise<AxiosResponse<IResponseTollFreeOptInUpload>> {
    return baseApi.post('organization/opt-in-data/files/upload', params, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  deleteLocalNumberAttachment(
    params: IParamsDeleteAttachment
  ): Promise<AxiosResponse<{ deleted: boolean }>> {
    return baseApi.delete('compliance/services/campaign/opt-in-files', { params })
  }

  deleteTollFreeAttachment(
    params: IParamsTollFreeDeleteAttachment
  ): Promise<AxiosResponse<{ deleted: boolean }>> {
    return baseApi.delete('organization/opt-in-data/files', { params })
  }

  submitLocalNumberApplication(
    params: IParamsSubmitLocalNumberApplication
  ): Promise<AxiosResponse> {
    return baseApi.post('ten-dlc/summary/submitted', params)
  }

  getStockExchanges(): Promise<AxiosResponse<IResponseStockExchanges[]>> {
    return baseApi.get('compliance/enum/stock-exchanges')
  }

  closeComplianceBannersSummary(): Promise<AxiosResponse<IResponseComplianceSummary>> {
    return baseApi.post('ten-dlc/summary/close-banners')
  }

  closeComplianceTaxIdBannerSummary(): Promise<AxiosResponse<IResponseComplianceSummary>> {
    return baseApi.post('ten-dlc/summary/close-tax-id-banner')
  }

  generateTollFreeAIDesc(
    params: IParamsGenerateAIDesc
  ): Promise<AxiosResponse<IResponseGenerateAI>> {
    return baseApi.get('ai/api/toll-free-verification/description', {
      params,
    })
  }

  generateTollFreeAIMessage(
    params: IParamsGenerateAIMessage
  ): Promise<AxiosResponse<IResponseGenerateAI>> {
    return baseApi.get('ai/api/toll-free-verification/sample-message', {
      params,
    })
  }

  generate10DLCAIDesc(
    params: IParamsGenerate10DLCAIDesc
  ): Promise<AxiosResponse<IResponseGenerate10DLCAIDesc>> {
    return baseApi.post('compliance-ai/generate/use_case_description', params)
  }

  generate10DLCAIMessages(
    params: IParamsGenerate10DLCAIMessage
  ): Promise<AxiosResponse<IResponseGenerate10DLCAIMessage>> {
    return baseApi.post('compliance-ai/generate/sample_messages', params)
  }

  getFreeTollFreeNumbers(
    params: IParamsGetFreeTollFreeNumbers
  ): Promise<AxiosResponse<IResponseFreeTollFreeNumber[]>> {
    return baseApi.post('numbers/search', params)
  }
  analyze10DLCAI(params: IParamsAnalyze10DLCAI): Promise<AxiosResponse<IResponseAnalyze10DLCAI>> {
    return baseApi.post('compliance-ai/analyze', params)
  }

  addTollFreeNumberToExistingInbox(
    id: number,
    params: IParamsAddTollFreeNumberToExistingInbox
  ): Promise<AxiosResponse<IResponseNumber[]>> {
    return baseApi.post(`teams/${id}/numbers`, params)
  }

  addTollFreeNumberWithNewInbox(params: IParamsAddTollFreeNumberWithNewInbox): Promise<
    AxiosResponse<{
      number: IResponseNumber
    }>
  > {
    return baseApi.post('teams', params)
  }

  submitTollFreeForReview(
    id: number,
    params: IParamsSubmitTollFreeForReview
  ): Promise<
    AxiosResponse<{
      data: IResponseTollFree
    }>
  > {
    return baseApi.put(`organization/toll-free/${id}`, params)
  }

  getNumberVendors(): Promise<AxiosResponse<IResponseNumberVendor[]>> {
    return baseApi.get('numbers/vendors')
  }

  getInternationalRegulatoryBundle(
    params: IParamsGetInternationalRegulatoryBundle
  ): Promise<AxiosResponse<IResponseInternationalRegulatoryBundle>> {
    return baseApi.get('compliance/services/international-regulatory-bundle', {
      params,
    })
  }

  getAllInternationalComplianceRegulations(): Promise<
    AxiosResponse<IResponseInternationalComplianceRegulations>
  > {
    return baseApi.get('compliance/international-compliance-regulations')
  }

  submitBusinessDataInternationalRegulatory(
    params: IParamsSubmitBusinessDataInternationalRegulatory
  ): Promise<AxiosResponse<IResponseSubmitDataInternationalRegulatory>> {
    return baseApi.post('compliance/services/international-regulatory-bundle/business', params)
  }

  submitAddressDataInternationalRegulatory(
    params: IParamsSubmitAddressDataInternationalRegulatory
  ): Promise<AxiosResponse<IResponseSubmitDataInternationalRegulatory>> {
    return baseApi.post('compliance/services/international-regulatory-bundle/address', params)
  }

  submitAuthorizedRepresentativeDataInternationalRegulatory(
    params: IParamsSubmitAuthorizedRepresentativeDataInternationalRegulatory
  ): Promise<AxiosResponse<IResponseSubmitDataInternationalRegulatory>> {
    return baseApi.post(
      'compliance/services/international-regulatory-bundle/authorized-representative',
      params
    )
  }

  submitTermsInternationalRegulatory(
    params: IParamsSubmitAInternationalRegulatory
  ): Promise<AxiosResponse<IResponseSubmitDataInternationalRegulatory>> {
    return baseApi.post('compliance/services/international-regulatory-bundle/submit', params)
  }
}

export const ComplianceApi = new Api()
